
import { useEffect, useState } from 'react';

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    
    // Create floating particles
    const createParticle = () => {
      const particle = document.createElement('div');
      particle.className = 'particle';
      
      const size = Math.random() * 8 + 4;
      const left = Math.random() * window.innerWidth;
      const duration = Math.random() * 4 + 6;
      
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      particle.style.left = `${left}px`;
      particle.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
      particle.style.borderRadius = '50%';
      particle.style.position = 'fixed';
      particle.style.top = '100vh';
      particle.style.pointerEvents = 'none';
      particle.style.animation = `float-up ${duration}s linear infinite`;
      
      document.body.appendChild(particle);
      
      setTimeout(() => {
        if (particle.parentNode) {
          particle.parentNode.removeChild(particle);
        }
      }, duration * 1000);
    };

    const particleInterval = setInterval(createParticle, 500);
    
    return () => {
      clearInterval(particleInterval);
    };
  }, []);

  return (
    <section 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(135deg, rgba(69, 82, 74, 0.85) 0%, rgba(69, 82, 74, 0.75) 50%, rgba(52, 73, 94, 0.8) 100%), url('https://readdy.ai/api/search-image?query=Futuristic%20chemical%20manufacturing%20facility%20with%20advanced%20automation%20systems%2C%20modern%20industrial%20complex%20with%20glass%20architecture%20and%20steel%20structures%2C%20chemical%20processing%20equipment%20with%20digital%20displays%2C%20clean%20energy%20powered%20chemical%20plant%20with%20solar%20panels%2C%20professional%20workers%20in%20high-tech%20environment%2C%20bright%20natural%20lighting%20with%20blue%20sky%20background%2C%20sustainable%20chemical%20production%20facility&width=1920&height=1080&seq=hero-modern-facility&orientation=landscape')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full opacity-20 animate-pulse" style={{ backgroundColor: '#45524A' }}></div>
        <div className="absolute top-40 right-20 w-24 h-24 rounded-full opacity-15 animate-bounce" style={{ backgroundColor: '#10B981' }}></div>
        <div className="absolute bottom-32 left-1/4 w-20 h-20 rounded-full opacity-10 animate-ping" style={{ backgroundColor: '#06B6D4' }}></div>
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
        {/* Main Content Container */}
        <div className="text-center space-y-12">
          {/* Hero Badge */}
          <div className="inline-flex items-center px-6 py-3 rounded-full border border-white/30 backdrop-blur-sm bg-white/10 text-white text-sm font-medium animate-fade-in">
            <i className="ri-award-line mr-2 text-emerald-300"></i>
            Leading Chemical Solutions Provider Since 2008
          </div>

          {/* Main Heading */}
          <div className="space-y-6 animate-slide-up">
            <h1 className="text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight text-white drop-shadow-2xl">
              Innovative
              <span className="block bg-gradient-to-r from-emerald-300 via-cyan-300 to-teal-300 bg-clip-text text-transparent animate-gradient-x bg-size-200">
                Chemical Solutions
              </span>
              <span className="block text-5xl lg:text-6xl xl:text-7xl mt-4" style={{ color: '#45524A' }}>
                for Modern Construction
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-100 max-w-4xl mx-auto leading-relaxed drop-shadow-lg">
              Transforming infrastructure development with cutting-edge construction chemicals, 
              advanced industrial solutions, and sustainable innovation across India.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center animate-fade-in-up">
            <button 
              className="group relative px-10 py-5 rounded-xl text-lg font-semibold transition-all duration-300 hover:scale-105 hover:shadow-2xl whitespace-nowrap cursor-pointer overflow-hidden"
              style={{ backgroundColor: '#45524A', color: 'white' }}
            >
              <span className="relative z-10">Explore Our Products</span>
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
            
            <button 
              className="group px-10 py-5 rounded-xl text-lg font-semibold border-2 border-white/50 text-white backdrop-blur-sm bg-white/10 transition-all duration-300 hover:scale-105 hover:bg-white hover:text-gray-900 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Get Consultation
            </button>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 pt-16 animate-fade-in-up">
            <div className="text-center group cursor-pointer">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center backdrop-blur-sm bg-white/10 group-hover:bg-white/20 transition-all duration-300">
                <i className="ri-building-line text-3xl text-emerald-300"></i>
              </div>
              <div className="text-4xl font-bold text-white drop-shadow-lg mb-2">500+</div>
              <div className="text-gray-200 font-medium">Projects Completed</div>
            </div>
            
            <div className="text-center group cursor-pointer">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center backdrop-blur-sm bg-white/10 group-hover:bg-white/20 transition-all duration-300">
                <i className="ri-time-line text-3xl text-cyan-300"></i>
              </div>
              <div className="text-4xl font-bold text-white drop-shadow-lg mb-2">15+</div>
              <div className="text-gray-200 font-medium">Years Experience</div>
            </div>
            
            <div className="text-center group cursor-pointer">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center backdrop-blur-sm bg-white/10 group-hover:bg-white/20 transition-all duration-300">
                <i className="ri-product-hunt-line text-3xl text-teal-300"></i>
              </div>
              <div className="text-4xl font-bold text-white drop-shadow-lg mb-2">100+</div>
              <div className="text-gray-200 font-medium">Quality Products</div>
            </div>
            
            <div className="text-center group cursor-pointer">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center backdrop-blur-sm bg-white/10 group-hover:bg-white/20 transition-all duration-300">
                <i className="ri-map-2-line text-3xl text-emerald-300"></i>
              </div>
              <div className="text-4xl font-bold text-white drop-shadow-lg mb-2">Pan-India</div>
              <div className="text-gray-200 font-medium">Distribution Network</div>
            </div>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-16 animate-fade-in-up">
            <div className="backdrop-blur-sm bg-white/10 rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 group cursor-pointer">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#45524A' }}>
                <i className="ri-flask-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">R&D Innovation</h3>
              <p className="text-gray-200 text-sm leading-relaxed">
                Advanced research facilities developing next-generation chemical solutions for modern construction challenges.
              </p>
            </div>
            
            <div className="backdrop-blur-sm bg-white/10 rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 group cursor-pointer">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#10B981' }}>
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Quality Assurance</h3>
              <p className="text-gray-200 text-sm leading-relaxed">
                International standards compliance with rigorous testing protocols ensuring superior product performance.
              </p>
            </div>
            
            <div className="backdrop-blur-sm bg-white/10 rounded-2xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 group cursor-pointer">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#06B6D4' }}>
                <i className="ri-customer-service-2-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Expert Support</h3>
              <p className="text-gray-200 text-sm leading-relaxed">
                Comprehensive technical assistance, training programs, and on-site support for optimal project outcomes.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-8 h-12 border-2 border-white/60 rounded-full flex justify-center backdrop-blur-sm">
          <div className="w-1.5 h-4 bg-white/80 rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
    </section>
  );
}
